import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, API_ENDPOINTS } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('authToken');
    const userInfo = localStorage.getItem('userInfo');
    
    if (token && userInfo) {
      try {
        const parsedUser = JSON.parse(userInfo);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing user info:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password, userType = 'superadmin') => {
    try {
      setLoading(true);
      
      // Use the login endpoint from API_ENDPOINTS
      let loginEndpoint = API_ENDPOINTS.LOGIN;
      
      const requestData = {
        email,
        password,
        userType: userType || 'employee'
      };
      
      console.log('🚀 Login Request:', {
        endpoint: loginEndpoint,
        data: { email, password: '***' },
        userType,
        baseURL: authApi.defaults.baseURL
      });
      
      const response = await authApi.post(loginEndpoint, requestData);
      
      console.log('✅ Login Response:', response.data);

      const { token, accessToken, user: userData } = response.data;
      
      // Clear any existing data first to prevent caching issues
      localStorage.clear();
      
      // Store token and user info (use accessToken if available, otherwise use token)
      const authToken = accessToken || token;
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('userInfo', JSON.stringify(userData));
      
      console.log('🔑 Token stored for company:', userData.companyId || userData.id);
      console.log('👤 User data stored:', userData);
      
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('❌ Login error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
          headers: error.config?.headers
        }
      });
      
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Login failed. Please try again.';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Clear all localStorage data to prevent token caching
    localStorage.clear();
    setUser(null);
    setIsAuthenticated(false);
    
    // Force page reload to clear any cached data
    window.location.reload();
  };

  const register = async (userData, userType = 'superadmin') => {
    try {
      setLoading(true);
      
      let registerEndpoint = '/api/auth/register-superadmin';
      
      const response = await authApi.post(registerEndpoint, userData);
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    register,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
